import type { PracticeLocale } from "./practiceLocale";
import type { PracticeProblemPart } from "./practiceParts";
import {
  isPracticeInstanceId,
  type VerifiedDrillExercise,
} from "./practiceVerifiedDrills";

export type GeneratedPracticeExercise = {
  prompt: string;
  answer?: string;
};

export type PracticeAskParsed = {
  tip: string;
  exercises: GeneratedPracticeExercise[];
};

const MORE_EXERCISES_RE = new RegExp(
  [
    "more\\s+(?:\\d+\\s+)?(?:exercises?|problems?|drills?|practice|questions?)",
    "another\\s+(?:one|exercise|problem|drill|question)",
    "write them on (?:my )?(?:page|board|sheet)",
    "write (?:it|them) on (?:the )?(?:page|board|sheet)",
    "generate (?:more )?(?:exercises?|problems?|drills?)",
    "more like this",
    "3rd[- ]grade (?:multiplication|drills?|exercises?)",
    "third[- ]grade (?:multiplication|drills?|exercises?)",
    "עוד\\s+תרגיל",
    "עוד\\s+תרגילים",
    "עוד\\s+תרגילי",
    "תרגילי\\s+(?:כפל|חילוק|חיבור|חיסור)",
    "כתוב(?:י)?[\\s\\S]{0,24}על\\s+(?:הדף|הלוח)",
    "תן(?:י)?\\s+לי\\s+עוד",
    "עוד\\s+כפל",
    "עוד\\s+כמו\\s+זה",
  ].join("|"),
  "i",
);

export function isMoreExercisesAsk(question: string): boolean {
  const q = question.replace(/\s+/g, " ").trim();
  if (!q) return false;
  return MORE_EXERCISES_RE.test(q);
}

export function moreExercisesAskText(locale: PracticeLocale): string {
  return locale === "he"
    ? "עוד תרגילים כמו זה, כתוב על הלוח"
    : "More exercises like this. Write them on my board.";
}

export function pathDrillAskText(
  locale: PracticeLocale,
  skill: string,
  subject: string,
): string {
  const topic = [skill, subject].filter(Boolean).join(" ").trim() || "math";
  if (locale === "he") {
    const heTopic = (skill || subject || "math").trim();
    return `כתוב 3 תרגילי ${heTopic} על הלוח`;
  }
  return `Generate 3 ${topic} drills. Write them on my board.`;
}

export function generatedExercisesTip(
  count: number,
  firstPartId: string,
  locale: PracticeLocale,
): string {
  if (locale === "he") {
    return `כתבתי ${count} תרגילים על הלוח. התחילו ב-(${firstPartId}).`;
  }
  return `I wrote ${count} exercises on the board. Start with (${firstPartId}).`;
}

export function practiceAskLogOutcome(
  generatedExerciseCount?: number,
): string {
  if (generatedExerciseCount && generatedExerciseCount > 0) {
    return `generated:${generatedExerciseCount}`;
  }
  return "answered";
}

export const GENERATED_DRILL_MAX_COLS = 20;
export const GENERATED_DRILL_ROW_GAP_MIN = 1;
export const GENERATED_DRILL_ROW_GAP_MAX = 4;

export function generatedDrillColSpan(
  prompt: string,
  colsAvailable: number,
): number {
  return Math.min(
    Math.max(8, prompt.trim().length + 2),
    Math.max(1, colsAvailable),
    GENERATED_DRILL_MAX_COLS,
  );
}

/**
 * Blank rows after existing work / between new stems, taken from how many
 * rows the student already used for the first problem.
 */
export function deducedDrillRowGap(occupiedRows: number): number {
  if (!Number.isFinite(occupiedRows) || occupiedRows < 1) {
    return GENERATED_DRILL_ROW_GAP_MIN;
  }
  return Math.min(
    GENERATED_DRILL_ROW_GAP_MAX,
    Math.max(GENERATED_DRILL_ROW_GAP_MIN, Math.round(occupiedRows)),
  );
}

/** Skip empty rows after the last mark so drills are not stacked flush. */
export function spacedDrillRow(
  nextFree: number | null,
  gap = GENERATED_DRILL_ROW_GAP_MIN,
  rowsNum = 40,
): number | null {
  if (nextFree == null) return null;
  const spaced = nextFree + gap;
  return spaced < rowsNum ? spaced : nextFree;
}

export function nextGeneratedPartId(parts: PracticeProblemPart[]): string {
  const nums = parts
    .map((part) => Number.parseInt(part.id, 10))
    .filter((n) => Number.isFinite(n) && n > 0);
  const max = nums.length ? Math.max(...nums) : parts.length;
  return String(max + 1);
}

export function mergeGeneratedParts(
  existing: PracticeProblemPart[],
  drills: Array<VerifiedDrillExercise | string>,
  fallbackFirstText = "Whole problem",
): {
  parts: PracticeProblemPart[];
  added: PracticeProblemPart[];
  firstNewId: string;
} {
  const clean = drills.flatMap((item) => {
    if (typeof item === "string") return [];
    const prompt = item.prompt.trim();
    const instanceId = item.instanceId.trim();
    if (!prompt || !isPracticeInstanceId(instanceId)) return [];
    return [{ prompt, instanceId }];
  });
  const parts = existing.length
    ? existing.map((part) => ({ ...part }))
    : [{ id: "1", text: fallbackFirstText }];
  const added: PracticeProblemPart[] = [];
  for (const drill of clean) {
    const id = nextGeneratedPartId(parts);
    const part = { id, text: drill.prompt, instanceId: drill.instanceId };
    parts.push(part);
    added.push(part);
  }
  return {
    parts,
    added,
    firstNewId: added[0]?.id ?? parts[parts.length - 1]?.id ?? "1",
  };
}

function clipPrompt(text: string): string {
  return text.replace(/\s+/g, " ").trim().slice(0, 200);
}

export function parseAskResponse(raw: string): PracticeAskParsed {
  const text = (raw ?? "").trim();
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    return { tip: text.replace(/\s+/g, " ").trim(), exercises: [] };
  }
  try {
    const parsed = JSON.parse(jsonMatch[0]) as {
      tip?: unknown;
      exercises?: unknown;
    };
    const tip =
      typeof parsed.tip === "string"
        ? parsed.tip.trim().replace(/\s+/g, " ")
        : "";
    const exercises: GeneratedPracticeExercise[] = [];
    if (Array.isArray(parsed.exercises)) {
      for (const item of parsed.exercises) {
        if (!item || typeof item !== "object") continue;
        const rec = item as {
          prompt?: unknown;
          text?: unknown;
          answer?: unknown;
        };
        const prompt = clipPrompt(
          typeof rec.prompt === "string"
            ? rec.prompt
            : typeof rec.text === "string"
              ? rec.text
              : "",
        );
        if (!prompt) continue;
        const answer =
          typeof rec.answer === "string" && rec.answer.trim()
            ? rec.answer.trim().slice(0, 80)
            : undefined;
        exercises.push(answer ? { prompt, answer } : { prompt });
        if (exercises.length >= 5) break;
      }
    }
    return { tip, exercises };
  } catch {
    return { tip: text.replace(/\s+/g, " ").trim(), exercises: [] };
  }
}
